import { useEffect, useState } from "react";

import "./ThemeToggle.css";

type Theme = "light" | "dark" | "system";

const KEY = "castflow-theme";

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    // Private windows and blocked site data both throw here. Falling back to
    // system is the right answer either way.
  }
  return "system";
}

function apply(theme: Theme) {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

/** The design ships both themes, so this decides which one is shown.
 *  "system" removes the attribute and lets prefers-color-scheme decide. */
export default function ThemeToggle({
  variant = "icon",
}: {
  variant?: "icon" | "button" | "switch";
}) {
  const [theme, setTheme] = useState<Theme>(read);

  useEffect(() => {
    apply(theme);
    try {
      if (theme === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, theme);
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, [theme]);

  const next: Theme = theme === "dark" ? "light" : "dark";
  const action = theme === "dark" ? "Disable dark mode" : "Enable dark mode";

  return (
    <button
      className={[
        "themetoggle",
        variant === "button" ? "themetoggle--button" : "",
        variant === "switch" ? "themetoggle--switch" : "",
        theme === "dark" ? "themetoggle--on" : "",
      ].filter(Boolean).join(" ")}
      onClick={() => setTheme(next)}
      aria-label={action}
      title={action}
      type="button"
    >
      {theme === "dark" ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 1v1.6M8 13.4V15M15 8h-1.6M2.6 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2 3.1 3.1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M13.5 9.6A5.8 5.8 0 0 1 6.4 2.5a5.8 5.8 0 1 0 7.1 7.1Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {variant === "button" && <span>{action}</span>}
    </button>
  );
}
